/**
 * trackingUi.jsx - the small pieces the Tracking views share: a figure card
 * with its "?" (what the number is and where it comes from), a progress bar,
 * an empty state that says what to do next, a segmented switch, a number
 * cell, the master's expense columns, and the hours calculator of the
 * master's INFO sheet (mirror of server lib/trackingEngine.js splitDay - a
 * typing aid; what is saved is what is typed).
 */
import { Explainer } from '../ui/Explainer';
import { GREEN, LINE, MUTED, td, th } from '../quotes/quoteUi';

export const EXPENSE_COLS = ['airfare', 'hotel', 'car', 'meals', 'misc', 'other'];
export const EXPENSE_LABELS = { airfare: 'Airfare', hotel: 'Hotel', car: 'Car/Taxi', meals: 'Meals', misc: 'Misc', other: 'Other' };

/** The project's expense handling fee as text: '8.5%' (Rev 1.7.x), '0%' (Rev 1.8 - none). */
export const feeText = (totals) => `${Number(((totals && totals.feeRate !== undefined ? totals.feeRate : 0.085) * 100).toFixed(2))}%`;

export function Figure({ label, value, tone, title }) {
  return (
    <div title={title} style={{ background: tone || '#F6F8F3', borderRadius: 8, padding: '8px 12px', minWidth: 110 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#1F2328', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

/**
 * A headline number: label, the value, one plain-English line under it, and
 * a "?" that says what it is (`tip`) and where it comes from (`from`).
 */
export function Stat({ label, value, note, tip, from, tone, accent, big }) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{
        background: tone || '#fff',
        border: `1px solid ${LINE}`,
        borderRadius: 10,
        padding: '10px 14px',
        minWidth: 0,
        flex: '1 1 170px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          fontSize: 10,
          fontWeight: 800,
          color: MUTED,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        <span>{label}</span>
        {(tip || from) && (
          <Explainer title={label} formula={from ? `Comes from: ${from}` : undefined}>
            {tip}
          </Explainer>
        )}
      </div>
      <div
        style={{ fontSize: big ? 22 : 17, fontWeight: 800, color: accent || '#1F2328', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}
      >
        {value}
      </div>
      {note && <div style={{ fontSize: 11, color: MUTED, marginTop: 2, lineHeight: 1.35 }}>{note}</div>}
    </div>
  );
}

/** A heading with its "?" - for table and panel titles. */
export function Hint({ title, tip, from }) {
  return (
    <Explainer title={title} formula={from ? `Comes from: ${from}` : undefined} align="left">
      {tip}
    </Explainer>
  );
}

/** A thin progress bar: share 0..1 (more than 1 shows red), an optional marker (0..1, e.g. time elapsed). */
export function Bar({ share, marker, label, color }) {
  const s = Number.isFinite(share) ? share : 0;
  const over = s > 1;
  return (
    <div
      aria-label={label}
      role="img"
      style={{ position: 'relative', height: 8, background: '#EEF1EA', borderRadius: 4, overflow: 'visible', minWidth: 60 }}
    >
      <div
        style={{
          width: `${Math.min(100, Math.max(0, s * 100))}%`,
          height: '100%',
          borderRadius: 4,
          background: over ? '#C62828' : color || GREEN,
        }}
      />
      {Number.isFinite(marker) && (
        <div
          title="Time elapsed"
          style={{
            position: 'absolute',
            top: -3,
            left: `calc(${Math.min(100, Math.max(0, marker * 100))}% - 1px)`,
            width: 2,
            height: 14,
            background: '#1F2328',
            borderRadius: 1,
          }}
        />
      )}
    </div>
  );
}

/** An empty table / panel: what is missing and the one thing to do next. */
export function Empty({ text, action, onAction }) {
  return (
    <div
      style={{
        padding: '14px 12px',
        fontSize: 12,
        color: MUTED,
        background: '#FAFBF7',
        border: `1px dashed ${LINE}`,
        borderRadius: 8,
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <span>{text}</span>
      {action && onAction && (
        <button
          type="button"
          onClick={onAction}
          style={{
            border: 0,
            background: 'none',
            padding: 0,
            color: GREEN,
            fontWeight: 800,
            fontSize: 12,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {action} &rarr;
        </button>
      )}
    </div>
  );
}

/** A segmented switch (the lens toggle, the sub-views). options [{ value, label, badge? }]. */
export function Segmented({ options, value, onChange, label, size = 'md' }) {
  return (
    <div
      role="tablist"
      aria-label={label}
      style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 2, background: '#EEF1EA', borderRadius: 8, padding: 3 }}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            style={{
              border: 0,
              borderRadius: 6,
              padding: size === 'sm' ? '5px 10px' : '7px 14px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: size === 'sm' ? 11 : 12,
              fontWeight: on ? 800 : 600,
              background: on ? '#fff' : 'transparent',
              color: on ? GREEN : '#4B5563',
              boxShadow: on ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {o.label}
            {o.badge ? (
              <span
                style={{
                  background: o.badgeTone || '#B45309',
                  color: '#fff',
                  borderRadius: 9,
                  padding: '0 6px',
                  fontSize: 9.5,
                  fontWeight: 800,
                }}
              >
                {o.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** A table head row; cost columns are left out by the caller. */
export const Head = ({ cols }) => (
  <thead>
    <tr>
      {cols.map((c, i) => (
        <th key={`${c}${i}`} style={{ ...th, textAlign: i === 0 ? 'left' : 'right' }}>
          {c}
        </th>
      ))}
    </tr>
  </thead>
);

export const Num = ({ v, strong, color }) => (
  <td
    style={{
      ...td,
      fontSize: 11.5,
      textAlign: 'right',
      fontWeight: strong ? 800 : 500,
      color,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    }}
  >
    {v}
  </td>
);

/** A day's total hours as NT / OT / DT: weekday 8 + OT, Saturday OT, Sunday or a holiday DT. */
export function splitDay(hours, iso, holiday = false) {
  const h = Math.max(0, Number(hours) || 0);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return { nt: h, ot: 0, dt: 0 };
  const g = new Date(`${iso}T00:00:00Z`).getUTCDay();
  if (holiday || g === 0) return { nt: 0, ot: 0, dt: h };
  if (g === 6) return { nt: 0, ot: h, dt: 0 };
  return { nt: Math.min(h, 8), ot: Math.max(h - 8, 0), dt: 0 };
}

/**
 * Rows pasted from Excel (a tracking sheet's columns) -> import rows:
 * Date, Engineer, PO Line, Task, NT, OT, DT, Expense (Yes / No) - tab or comma
 * separated, a header row skipped.
 */
export function parsePastedRows(text) {
  const out = [];
  for (const line of String(text || '').split(/\r?\n/)) {
    if (!line.trim()) continue;
    const c = line.split(line.includes('\t') ? '\t' : ',').map((x) => x.trim());
    if (/^date$/i.test(c[0])) continue;
    const [date, person, poLine, task, nt, ot, dt, expense] = c;
    out.push({
      date,
      person,
      poLine,
      task,
      nt: Number(nt) || 0,
      ot: Number(ot) || 0,
      dt: Number(dt) || 0,
      expense: /^y(es)?$/i.test(expense || ''),
    });
  }
  return out;
}
